import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { PATHS } from "../../constants/paths";
import { resolveReturnTo, returnState } from "../../utils/returnTo";
import {
  auditLine,
  isValidTag,
  messageTemplateApi,
  normalizeTag,
} from "../../utils/messageTemplateApi";

const useStyles = makeStyles({
  page: {
    backgroundColor: "#f7f9fc",
    minHeight: "calc(100vh - 64px)",
    paddingTop: "16px",
    paddingBottom: "32px",
  },
  back: { minHeight: "44px", marginBottom: "8px" },
  title: { fontSize: "28px", fontWeight: 700, color: "#101828" },
  subtitle: {
    marginTop: "4px",
    marginBottom: "16px",
    fontSize: "14px",
    color: "#475467",
    maxWidth: "760px",
  },
  filters: { padding: "16px", marginBottom: "12px" },
  cell: { padding: "8px", fontSize: "13px", verticalAlign: "top" },
  headerCell: {
    padding: "8px",
    fontSize: "12px",
    fontWeight: 700,
    backgroundColor: "#f2f5fa",
  },
  mono: { fontFamily: "monospace", fontSize: "12px" },
  touch: { minHeight: "44px" },
  field: { marginTop: "12px" },
  empty: { textAlign: "center", padding: "32px 12px", color: "#475467" },
});

const STATUS_LABELS = {
  PENDING: "Waiting",
  MAPPED: "Connected",
  REJECTED: "Rejected",
};

/**
 * Variables admins used that the code did not supply when they saved. Developers connect them
 * (after adding the field to a view model and deploying, or by pointing them at an existing
 * variable, which rewrites the templates) or reject them.
 */
const VariableRequests = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("PENDING");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [decision, setDecision] = useState({
    status: "MAPPED",
    mappedTo: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    messageTemplateApi
      .variableRequests(status)
      .then((data) => setRows(data || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (row) => {
    setResolving(row);
    setDecision({ status: "MAPPED", mappedTo: row.tag, note: "" });
  };

  const mappedTo = normalizeTag(decision.mappedTo);
  const invalidTarget =
    decision.status === "MAPPED" && mappedTo !== "" && !isValidTag(mappedTo);

  const submit = async () => {
    setSaving(true);
    try {
      await messageTemplateApi.resolveVariableRequest(resolving.id, {
        status: decision.status,
        mappedTo: decision.status === "MAPPED" ? mappedTo || null : null,
        note: decision.note,
      });
      toast.success(
        decision.status === "MAPPED"
          ? mappedTo && mappedTo !== resolving.tag
            ? `Connected; every language now uses {{${mappedTo}}}.`
            : "Connected."
          : "Rejected.",
      );
      setResolving(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className={classes.page}>
      <Container maxWidth="xl">
        <Button
          className={classes.back}
          onClick={() =>
            navigate(resolveReturnTo(location, PATHS.MESSAGE_TEMPLATES))
          }
        >
          ← All message templates
        </Button>
        <Typography component="h1" className={classes.title}>
          Requested variables
        </Typography>
        <Typography className={classes.subtitle}>
          Tags admins used that the sending code does not supply. They are sent
          empty until connected. To connect one, add the field to the template's
          view model and deploy, then mark it connected; or connect it to an
          existing variable, which rewrites the tag in every language of the
          template.
        </Typography>

        <Paper className={classes.filters}>
          <TextField
            select
            id="mt-requests-status"
            label="Status"
            variant="outlined"
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ minWidth: 220 }}
          >
            <MenuItem value="PENDING">Waiting</MenuItem>
            <MenuItem value="MAPPED">Connected</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </TextField>
        </Paper>

        <TableContainer component={Paper}>
          <Table
            size="small"
            aria-label="Requested variables"
            aria-busy={loading}
          >
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Variable</TableCell>
                <TableCell className={classes.headerCell}>Template</TableCell>
                <TableCell className={classes.headerCell}>
                  What it should contain
                </TableCell>
                <TableCell className={classes.headerCell}>Requested</TableCell>
                <TableCell className={classes.headerCell}>Status</TableCell>
                <TableCell className={classes.headerCell}>
                  <span className="sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.empty}>
                    <CircularProgress aria-label="Loading requested variables" />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.empty}>
                    Nothing here.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell className={`${classes.cell} ${classes.mono}`}>
                      {`{{${r.tag}}}`}
                      {r.mappedTo && r.mappedTo !== r.tag && (
                        <div>→ {`{{${r.mappedTo}}}`}</div>
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Button
                        color="primary"
                        className={classes.touch}
                        onClick={() =>
                          navigate(
                            `${PATHS.MESSAGE_TEMPLATES}/${r.messageTemplateId}${
                              r.localeId
                                ? `/${encodeURIComponent(r.localeId)}`
                                : ""
                            }`,
                            { state: returnState(location) },
                          )
                        }
                      >
                        {r.templateName}
                        {r.templateDescription
                          ? ` · ${r.templateDescription}`
                          : ""}
                      </Button>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {r.description || "—"}
                      {r.resolutionNote && (
                        <div style={{ color: "#475467" }}>
                          Note: {r.resolutionNote}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {auditLine(r.requestedAt, r.requestedByName)}
                      {r.localeId ? ` · ${r.localeId}` : ""}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[r.status] || r.status}
                      />
                      {r.resolvedAt && (
                        <div style={{ color: "#475467" }}>
                          {auditLine(r.resolvedAt, r.resolvedByName)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {r.status === "PENDING" && (
                        <Button
                          variant="outlined"
                          className={classes.touch}
                          onClick={() => open(r)}
                          aria-label={`Resolve ${r.tag}`}
                        >
                          Resolve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={Boolean(resolving)}
          onClose={() => setResolving(null)}
          aria-labelledby="mt-resolve-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="mt-resolve-title">
            Resolve {resolving && `{{${resolving.tag}}}`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              “Connected” needs the variable to exist in the deployed code;
              otherwise the server refuses it.
            </DialogContentText>
            <TextField
              select
              fullWidth
              id="mt-resolve-decision"
              label="Decision"
              variant="outlined"
              className={classes.field}
              value={decision.status}
              onChange={(e) =>
                setDecision((d) => ({ ...d, status: e.target.value }))
              }
            >
              <MenuItem value="MAPPED">Connected</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
            </TextField>
            {decision.status === "MAPPED" && (
              <TextField
                fullWidth
                id="mt-resolve-mapped-to"
                label="Variable the code supplies"
                variant="outlined"
                className={classes.field}
                value={decision.mappedTo}
                onChange={(e) =>
                  setDecision((d) => ({ ...d, mappedTo: e.target.value }))
                }
                error={invalidTarget}
                helperText={
                  invalidTarget
                    ? "Letters, digits and _ only, with dots between parts"
                    : "Keep the requested name if the code now supplies it; a different name rewrites every language of the template."
                }
                inputProps={{ maxLength: 200, spellCheck: false }}
              />
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              id="mt-resolve-note"
              label="Note (optional)"
              variant="outlined"
              className={classes.field}
              value={decision.note}
              onChange={(e) =>
                setDecision((d) => ({ ...d, note: e.target.value }))
              }
              inputProps={{ maxLength: 1000 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              className={classes.touch}
              onClick={() => setResolving(null)}
            >
              Cancel
            </Button>
            <Button
              className={classes.touch}
              color="primary"
              variant="contained"
              onClick={submit}
              disabled={saving || invalidTarget}
            >
              {saving ? "Saving…" : "Save decision"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default VariableRequests;
